import { useEffect, useState } from "react";

export const ITEMIZED_BILL_PROMO_SESSION_KEY =
  "promo-itemized-bill-session-start";
export const ITEMIZED_BILL_PROMO_DURATION_MS = 5 * 60 * 1000;

const getItemizedBillPromoSessionStart = (): number => {
  if (typeof window === "undefined") {
    return Date.now();
  }

  const existing = window.sessionStorage.getItem(
    ITEMIZED_BILL_PROMO_SESSION_KEY,
  );
  const parsed = existing ? Number(existing) : NaN;

  if (Number.isFinite(parsed) && parsed > 0) {
    return parsed;
  }

  const now = Date.now();
  window.sessionStorage.setItem(ITEMIZED_BILL_PROMO_SESSION_KEY, String(now));

  return now;
};

const resetItemizedBillPromoSessionStart = (): number => {
  const now = Date.now();
  if (typeof window !== "undefined") {
    window.sessionStorage.setItem(ITEMIZED_BILL_PROMO_SESSION_KEY, String(now));
  }

  return now;
};

const isWithinPromoWindow = (promoStart: number) =>
  Date.now() - promoStart < ITEMIZED_BILL_PROMO_DURATION_MS;

type UseItemizedBillPromoSessionOptions = {
  /** Starts a fresh promo window on mount (cockpit dashboard). */
  resetOnMount?: boolean;
  /** When false, the hook reports the window as closed and skips timer setup. */
  enabled?: boolean;
};

export const useItemizedBillPromoSession = ({
  resetOnMount = false,
  enabled = true,
}: UseItemizedBillPromoSessionOptions = {}) => {
  const [promoStart] = useState(() => {
    if (!enabled) {
      return 0;
    }

    return resetOnMount
      ? resetItemizedBillPromoSessionStart()
      : getItemizedBillPromoSessionStart();
  });

  const [isPromoOpen, setIsPromoOpen] = useState(() => {
    if (!enabled) {
      return false;
    }

    return isWithinPromoWindow(promoStart);
  });

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const remaining =
      ITEMIZED_BILL_PROMO_DURATION_MS - (Date.now() - promoStart);
    if (remaining <= 0) {
      setIsPromoOpen(false);

      return;
    }

    const id = window.setTimeout(() => setIsPromoOpen(false), remaining);

    return () => window.clearTimeout(id);
  }, [enabled, promoStart]);

  return { isPromoOpen, promoStart };
};
